import { LIBRARY_REVIEWED, STANDARD_DISCLAIMER } from './taxonomy'
import type { RegulatoryRecord } from './types'

const reviewed = LIBRARY_REVIEWED
const disclaimer = STANDARD_DISCLAIMER

export const FINANCE_RECORDS: RegulatoryRecord[] = [
  {
    id: 'cbuae-c-8-2025',
    slug: 'cbuae-climate-related-financial-risk-regulation-c-8-2025',
    title: 'Climate-related Financial Risk Management Regulation (CBUAE Circular C 8/2025)',
    shortTitle: 'CBUAE Regulation C 8/2025',
    authority: 'Central Bank of the United Arab Emirates',
    jurisdiction: ['federal'],
    categories: ['climate_financial_risk', 'sustainable_finance'],
    instrumentType: 'financial_regulation',
    summary:
      'Binding CBUAE regulation on climate-related financial risk management for banks and insurance companies. It requires governance, a structured risk process, data, internal reporting, risk-appetite limits, mitigation plans and scenario analysis at least annually. It comes into effect upon publication in the Official Gazette. The Rulebook lists the status as In-Force. A separate gazette date was not identified on the reviewed Rulebook page, so no gazette calendar date is stored. Institutions that cannot demonstrate full compliance must submit a remediation plan. The Central Bank decides whether that plan is adequate. No fixed filing day for the plan was stated.',
    requirementsSummary: [
      'Applies to banks and insurance companies. Groups with significant relationships must comply on a solo and group-wide basis.',
      'The Central Bank may later extend the regulation to other licensed financial institutions. That extension was not identified as already in force.',
      'Proportionality is recognised, including for branches of foreign banks.',
      'Climate-related financial risk must sit in the risk framework, with identification, data, measurement, reporting, control and mitigation.',
      'Scenario analysis must be conducted at least annually, and inside ICAAP where the institution is required to run ICAAP.',
      'Article 8: effective upon publication in the Official Gazette. Non-compliant institutions must submit a plan explaining the gap and the steps to full compliance.',
    ],
    applicabilitySummary:
      'Likely relevant to UAE banks and insurance companies. Other financial firms are not automatically in scope. Non-financial companies are not the regulated entities, though their banks may ask them for climate data.',
    applicabilityConditions: [
      'The entity is a bank or an insurance company within CBUAE’s scope.',
      'Foreign branches and subsidiaries should confirm how proportionality and group-level application are being applied.',
      'No confirmed calendar deadline for the remediation plan was identified.',
    ],
    practicalImplications: [
      'This is a prudential risk-management regulation, not a sustainability report for non-financial companies.',
      'Annual scenario analysis is a recurring process duty. The regulation does not name a calendar day.',
      'Borrowers may feel this indirectly through credit due diligence. That indirect effect is not the same as being in scope of C 8/2025.',
    ],
    recommendedActions: [
      'Banks and insurers: map the regulation to board governance, risk appetite, data and the annual scenario cycle.',
      'If full compliance is not in place, prepare the remediation plan the regulation requires and agree its contents with counsel and the supervisor relationship team.',
      'Other firms: do not treat C 8/2025 as your own filing obligation.',
    ],
    entityTypes: ['Banks', 'Insurance companies', 'UAE institutions required to comply on a solo and group basis'],
    obligationLevel: 'mandatory',
    listingScope: 'not_specific',
    financialScope: 'financial',
    establishmentScope: 'not_specific',
    sectors: ['banking', 'insurance'],
    publicationDate: '2025-07-08',
    deadlines: [
      {
        date: '2025-07-08',
        label: 'Circular issue date (C 8/2025)',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://rulebook.centralbank.ae/en/node/6431',
        notes: 'Issue date of the circular. Article 8 makes entry into force depend on Official Gazette publication. The Rulebook status is In-Force. A separate gazette date was not identified.',
      },
      {
        label: 'Entry into force upon publication in the Official Gazette. No separate gazette date identified on the reviewed Rulebook page.',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://rulebook.centralbank.ae/en/rulebook/article-8-publication-and-application',
      },
      {
        label: 'Scenario analysis at least annually. No calendar day identified.',
        type: 'reporting_period',
        certainty: 'confirmed',
        sourceUrl: 'https://rulebook.centralbank.ae/en/rulebook/article-4-risk-management',
      },
      {
        label: 'Remediation plan for institutions that cannot demonstrate full compliance. No calendar day identified. The Central Bank assesses the plan.',
        type: 'filing',
        certainty: 'confirmed',
        sourceUrl: 'https://rulebook.centralbank.ae/en/rulebook/article-8-publication-and-application',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'CBUAE Rulebook — Climate-related Financial Risk Management Regulation',
        url: 'https://rulebook.centralbank.ae/en/node/6431',
        sourceType: 'primary',
        relevantSection: 'C 8/2025 issued 8 July 2025; status In-Force; scope paragraph 8',
      },
      {
        title: 'CBUAE Rulebook — Article 8 Publication and Application',
        url: 'https://rulebook.centralbank.ae/en/rulebook/article-8-publication-and-application',
        sourceType: 'primary',
        relevantSection: 'Effective on Official Gazette publication; remediation plan',
      },
      {
        title: 'CBUAE Rulebook — Article 4 Risk Management',
        url: 'https://rulebook.centralbank.ae/en/rulebook/article-4-risk-management',
        sourceType: 'primary',
        relevantSection: 'Annual scenario analysis',
      },
    ],
    relatedRecordIds: ['cbuae-sustainability-disclosure-2024', 'cbuae-climate-risk-principles-2023'],
    relevantSections: ['Introduction and scope', 'Article 4', 'Article 8'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description:
          'Initial navigator record. Scope limited to banks and insurers. Gazette calendar date intentionally left blank.',
        sourceUrl: 'https://rulebook.centralbank.ae/en/node/6431',
      },
    ],
    disclaimer,
  },
  {
    id: 'cbuae-sustainability-disclosure-2024',
    slug: 'cbuae-principles-sustainability-related-disclosures-2024',
    title: 'Principles for Sustainability-Related Disclosures for Reporting Entities (CBUAE, June 2024)',
    shortTitle: 'CBUAE sustainability disclosure principles',
    authority: 'Central Bank of the United Arab Emirates',
    jurisdiction: ['federal'],
    categories: ['esg_disclosure', 'sustainable_finance'],
    instrumentType: 'financial_regulation',
    summary:
      'In-force principles, issued on 14 June 2024, on sustainability-related disclosures by reporting entities in the CBUAE framework. They expect disclosures on governance, strategy, risk management, and metrics and targets where sustainability-related risks and opportunities are material. The text uses “should” and “would be expected”. It is not a rule that every UAE company must publish a sustainability report, and it does not state a universal filing day. Branches or subsidiaries of foreign entities may, unless otherwise determined, use group-level equivalent standards to avoid duplication.',
    requirementsSummary: [
      'Issued 14 June 2024. Rulebook status: In-Force.',
      'Disclosures should reflect governance, strategy, risk management, metrics and targets.',
      'Materiality, frequency, scope and granularity are expressly left to careful consideration. No single filing calendar was stated in the reviewed text.',
      'Additional specific requirements may apply depending on the type of reporting entity.',
      'Foreign branches and subsidiaries may implement the principles, or equivalent standards, at group level if applicable, unless otherwise determined.',
    ],
    applicabilitySummary:
      'Relevant to reporting entities in the CBUAE sustainability-disclosure framework, in practice banks, insurers and other entities the Central Bank treats as reporting entities. Non-financial companies are outside this instrument unless a separate requirement applies.',
    applicabilityConditions: [
      'The entity is a reporting entity for these principles.',
      'Content is principles-based. A missing calendar date is not an implied year-end deadline.',
    ],
    practicalImplications: [
      'Prepare governance, strategy, risk and metrics narratives if the institution is a reporting entity.',
      'Do not cite these principles as a federal ESG mandate for unlisted non-financial companies.',
    ],
    recommendedActions: [
      'Confirm with the compliance function whether the institution is a reporting entity and which reporting cycle the supervisor expects.',
      'Map existing climate and sustainability disclosures to governance, strategy, risk management, metrics and targets.',
    ],
    entityTypes: ['CBUAE reporting entities', 'Banks and insurers within the sustainable-finance framework'],
    obligationLevel: 'conditional',
    listingScope: 'not_specific',
    financialScope: 'financial',
    establishmentScope: 'not_specific',
    sectors: ['banking', 'insurance', 'other_financial'],
    publicationDate: '2024-06-14',
    deadlines: [
      {
        date: '2024-06-14',
        label: 'Issue date on the CBUAE Rulebook',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://rulebook.centralbank.ae/en/rulebook/principles-sustainability-related-disclosures',
        notes: 'No separate effective date or filing day was identified. Rulebook status is In-Force.',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'CBUAE Rulebook — Principles for Sustainability-Related Disclosures',
        url: 'https://rulebook.centralbank.ae/en/rulebook/principles-sustainability-related-disclosures',
        sourceType: 'primary',
        relevantSection: 'Issued 14 June 2024; status In-Force',
      },
    ],
    relatedRecordIds: ['cbuae-c-8-2025', 'cbuae-climate-risk-principles-2023', 'ifrs-s1-s2'],
    relevantSections: ['Application', 'Minimum expected disclosures'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. Classified as principles-based. No filing day added.',
        sourceUrl: 'https://rulebook.centralbank.ae/en/rulebook/principles-sustainability-related-disclosures',
      },
    ],
    disclaimer,
  },
  {
    id: 'cbuae-climate-risk-principles-2023',
    slug: 'cbuae-principles-climate-related-financial-risks-2023',
    title: 'Principles for the Effective Management of Climate-related Financial Risks',
    shortTitle: 'CBUAE climate-risk principles (2023)',
    authority: 'Central Bank of the United Arab Emirates / UAE Sustainable Finance Working Group',
    jurisdiction: ['federal'],
    categories: ['climate_financial_risk', 'sustainable_finance'],
    instrumentType: 'financial_regulation',
    summary:
      'Principles issued through the UAE Sustainable Finance Working Group and published on the CBUAE Rulebook. The Central Bank’s press release is dated 13 November 2023. C 8/2025 describes these principles as issued in November 2023. They cover board oversight, strategy, organisation, risk management, monitoring and reporting, capital and liquidity, and scenario analysis. They are principles. The later Regulation C 8/2025 is the binding risk-management regulation for banks and insurers.',
    requirementsSummary: [
      'Seven principles: oversight, strategy, responsibilities, risk framework, monitoring and reporting, capital and liquidity, and scenario analysis.',
      'The Rulebook marks the principles In-Force.',
      'They informed, and are distinct from, Regulation C 8/2025.',
    ],
    applicabilitySummary:
      'Relevant supervisory principles for financial institutions in the UAE sustainable-finance framework. They are not a reporting mandate for non-financial companies.',
    applicabilityConditions: [
      'The reader is a financial institution inside the framework that issued the principles.',
      'Binding operational duties for banks and insurers should be read in C 8/2025, not inferred only from the 2023 principles.',
    ],
    practicalImplications: [
      'Use the principles as the design baseline and C 8/2025 as the regulation where the institution is a bank or insurer.',
    ],
    recommendedActions: [
      'Map the seven principles to existing risk policies.',
      'Where C 8/2025 applies, test the map against that regulation rather than stopping at the principles.',
    ],
    entityTypes: ['Financial institutions in the UAE sustainable-finance framework'],
    obligationLevel: 'informational',
    listingScope: 'not_specific',
    financialScope: 'financial',
    establishmentScope: 'not_specific',
    sectors: ['banking', 'insurance', 'other_financial'],
    publicationDate: '2023-11-13',
    deadlines: [
      {
        date: '2023-11-13',
        label: 'CBUAE press-release date for the launch of the principles',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://www.centralbank.ae/en/news-and-publications/news-and-insights/press-release/uae-sustainable-finance-working-group-launches-the-principles-for-the-effective-management-of-climate-related-financial-risks/',
        notes: 'Launch communication date. A separate legal effective date was not identified.',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'CBUAE Rulebook — Principles for the effective management of climate-related financial risks',
        url: 'https://rulebook.centralbank.ae/en/rulebook/principles-effective-management-climate-related-financial-risks',
        sourceType: 'primary',
        relevantSection: 'Status In-Force; seven principles',
      },
      {
        title: 'CBUAE press release — 13 November 2023',
        url: 'https://www.centralbank.ae/en/news-and-publications/news-and-insights/press-release/uae-sustainable-finance-working-group-launches-the-principles-for-the-effective-management-of-climate-related-financial-risks/',
        sourceType: 'primary',
        relevantSection: 'Launch date',
      },
    ],
    relatedRecordIds: ['cbuae-c-8-2025', 'cbuae-sustainability-disclosure-2024'],
    relevantSections: ['Principles 1 to 7'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. Kept distinct from Regulation C 8/2025.',
        sourceUrl: 'https://rulebook.centralbank.ae/en/rulebook/principles-effective-management-climate-related-financial-risks',
      },
    ],
    disclaimer,
  },
]

export const MARKET_RECORDS: RegulatoryRecord[] = [
  {
    id: 'adx-esg-guidance',
    slug: 'adx-esg-disclosure-guidance',
    title: 'ADX ESG Disclosure Guidance for Listed Companies',
    shortTitle: 'ADX ESG disclosure guidance',
    authority: 'Abu Dhabi Securities Exchange (ADX)',
    jurisdiction: ['abu_dhabi'],
    categories: ['esg_disclosure', 'ghg', 'climate'],
    instrumentType: 'exchange_guidance',
    summary:
      'ADX guidance to help listed companies disclose ESG information. ADX describes the guidance as voluntary support for issuers. ADX’s 2024 sustainability report states that sustainability reporting is mandatory for local public joint stock companies under Securities and Commodities Authority governance rules, and that ADX encourages voluntary disclosure from other listed entities, including the Growth Market. The SCA instrument number and any filing calendar were not verified for this record, so they are not stated as facts here.',
    requirementsSummary: [
      'ADX publishes ESG disclosure guidance and metrics for listed companies, including environmental indicators such as greenhouse-gas emissions with reference to the GHG Protocol.',
      'The guidance is an exchange document. It is not federal climate legislation.',
      'ADX’s own 2024 sustainability report distinguishes SCA mandatory sustainability reports for local PJSCs from ADX voluntary guidance and from encouragement of other listed entities.',
      'No ADX filing day was confirmed in the pages reviewed.',
    ],
    applicabilitySummary:
      'Likely relevant to companies listed on ADX. Relevance to an IPO candidate depends on the target market. Unlisted companies are not the audience of this guidance.',
    applicabilityConditions: [
      'The company is listed on ADX, or is assessing ADX listing.',
      'Which metrics apply to which issuer category should be confirmed on the current ADX guidance.',
      'Any SCA mandatory report is a separate instrument and is not reproduced here.',
    ],
    practicalImplications: [
      'Listed issuers should obtain the current ADX metrics file and align owners for environmental, social and governance indicators.',
      'Do not convert this guidance into a federal GHG filing obligation.',
    ],
    recommendedActions: [
      'Download the current ADX ESG disclosure guidance and metrics from adx.ae.',
      'Confirm separately, with counsel or the sponsor, the SCA sustainability-report position for the issuer category.',
      'Do not invent a submission date.',
    ],
    entityTypes: ['ADX-listed companies', 'Companies preparing an ADX listing'],
    obligationLevel: 'conditional',
    listingScope: 'listed',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['cross_sector'],
    deadlines: [
      {
        label: 'No confirmed ADX filing deadline identified in the guidance pages reviewed.',
        type: 'filing',
        certainty: 'confirmed',
        sourceUrl: 'https://www.adx.ae/about-adx/sustainability/overview',
        notes: 'Absence of a date in this record is intentional. It is not a finding that no market practice exists.',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'ADX — Sustainability overview',
        url: 'https://www.adx.ae/about-adx/sustainability/overview',
        sourceType: 'primary',
      },
      {
        title: 'ADX — ESG Disclosure Guidance for Listed Companies (English PDF)',
        url: 'https://www.adx.ae/-/media/adx/related-documents/sustainability/esg-disclosure-guidance-for-listed-companies-eng.pdf',
        sourceType: 'primary',
        relevantSection: 'ESG metrics, including GHG emissions',
      },
      {
        title: 'ADX Sustainability Report 2024',
        url: 'https://www.adx.ae/-/media/adx/adx-sustainability-reports/adx-sustainability-report-2024-en.pdf',
        sourceType: 'primary',
        relevantSection: 'Statement on SCA sustainability reports for local PJSCs and on ADX voluntary guidance',
      },
    ],
    relatedRecordIds: ['adx-esg-metrics', 'dfm-esg-guidance', 'ifrs-s1-s2', 'gri-standards'],
    relevantSections: ['Environmental metrics', 'GHG emissions indicators'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description:
          'Initial navigator record. SCA mandate mentioned only as ADX’s description. SCA instrument number and any deadline left unverified.',
        sourceUrl: 'https://www.adx.ae/about-adx/sustainability/overview',
      },
    ],
    disclaimer,
  },
  {
    id: 'adx-esg-metrics',
    slug: 'adx-esg-metrics',
    title: 'ADX and GCC exchanges ESG metrics',
    shortTitle: 'ADX ESG metrics',
    authority: 'Abu Dhabi Securities Exchange (ADX)',
    jurisdiction: ['abu_dhabi'],
    categories: ['esg_disclosure'],
    instrumentType: 'exchange_guidance',
    summary:
      'ADX metrics page describing the GCC Exchanges Committee unified ESG disclosure metrics published on 9 January 2023. ADX states that the set comprises 29 standards aligned with the World Federation of Exchanges and the Sustainable Stock Exchanges Initiative, covering topics such as GHG emissions, energy, water, gender pay, turnover, diversity, data privacy and ethics. This is a metric reference for issuer disclosure. It is not itself a federal law.',
    requirementsSummary: [
      'ADX page: GCC Exchanges Committee metrics published 9 January 2023.',
      '29 standards, as described by ADX, covering environmental, social and governance topics.',
      'No filing deadline is stated on the metrics page reviewed.',
    ],
    applicabilitySummary: 'Relevant to ADX-listed issuers using the exchange metric set. Confirm the current file before treating any indicator as required for a specific issuer category.',
    applicabilityConditions: ['Listing or intended listing on ADX.', 'Issuer category still to be checked against the current guidance.'],
    practicalImplications: ['Use the metric list as a disclosure checklist for an ADX sustainability report, alongside any SCA position verified separately.'],
    recommendedActions: ['Open the live ADX metrics page and the current guidance PDF together.', 'Assign an owner to each metric the company decides to report.'],
    entityTypes: ['ADX-listed companies'],
    obligationLevel: 'conditional',
    listingScope: 'listed',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['cross_sector'],
    publicationDate: '2023-01-09',
    deadlines: [
      {
        date: '2023-01-09',
        label: 'Date ADX gives for publication of the GCC Exchanges Committee unified ESG metrics',
        type: 'effective',
        certainty: 'confirmed',
        sourceUrl: 'https://www.adx.ae/en/about-adx/sustainability/esg-metrics',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'ADX — ESG metrics',
        url: 'https://www.adx.ae/en/about-adx/sustainability/esg-metrics',
        sourceType: 'primary',
        relevantSection: 'GCC metrics published 9 January 2023; 29 standards',
      },
      {
        title: 'ADX — ESG metrics (alternate overview path)',
        url: 'https://www.adx.ae/about-adx/sustainability/esg-metrics',
        sourceType: 'primary',
      },
    ],
    relatedRecordIds: ['adx-esg-guidance'],
    relevantSections: ['GCC ESG disclosure metrics'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record from the ADX metrics page. No filing day added.',
        sourceUrl: 'https://www.adx.ae/en/about-adx/sustainability/esg-metrics',
      },
    ],
    disclaimer,
  },
  {
    id: 'dfm-esg-guidance',
    slug: 'dfm-esg-reporting-guidance',
    title: 'Dubai Financial Market ESG sustainability guidance',
    shortTitle: 'DFM ESG guidance',
    authority: 'Dubai Financial Market (DFM)',
    jurisdiction: ['dubai'],
    categories: ['esg_disclosure'],
    instrumentType: 'exchange_guidance',
    summary:
      'DFM maintains an official ESG and sustainability section for the market. It is exchange guidance for the DFM context, not federal climate legislation. The page reviewed does not state a universal filing deadline, a metric count, or a rule that every Dubai company must report. Issuers should confirm the current guide version on dfm.ae.',
    requirementsSummary: [
      'Official DFM sustainability section is the primary pointer for current issuer materials.',
      'No filing deadline was identified on the official section reviewed for this record.',
      'Expectations depend on issuer status on DFM.',
    ],
    applicabilitySummary: 'Likely relevant to DFM-listed issuers. Not a default obligation for unlisted Dubai companies.',
    applicabilityConditions: ['The company is listed on DFM or is preparing a DFM listing.', 'Current guide version still to be confirmed on the exchange site.'],
    practicalImplications: ['Treat DFM materials as market disclosure guidance and keep them separate from MOCCAE climate duties.'],
    recommendedActions: ['Review the current DFM ESG sustainability page and any linked guide before preparing an issuer report.', 'Do not copy another exchange’s deadline onto DFM.'],
    entityTypes: ['DFM-listed issuers'],
    obligationLevel: 'conditional',
    listingScope: 'listed',
    financialScope: 'not_specific',
    establishmentScope: 'not_specific',
    sectors: ['cross_sector'],
    deadlines: [
      {
        label: 'No confirmed DFM filing deadline identified on the official sustainability section reviewed.',
        type: 'filing',
        certainty: 'confirmed',
        sourceUrl: 'https://www.dfm.ae/discover-dfm/esg-sustainability',
      },
    ],
    status: 'in_force',
    officialSources: [
      {
        title: 'DFM — ESG sustainability',
        url: 'https://www.dfm.ae/discover-dfm/esg-sustainability',
        sourceType: 'primary',
      },
    ],
    relatedRecordIds: ['adx-esg-guidance'],
    relevantSections: ['DFM ESG sustainability section'],
    amendments: [],
    lastReviewedDate: reviewed,
    verificationStatus: 'verified',
    recordVersion: 1,
    changeHistory: [
      {
        date: reviewed,
        description: 'Initial navigator record. Limited to the official DFM section. No deadline inferred.',
        sourceUrl: 'https://www.dfm.ae/discover-dfm/esg-sustainability',
      },
    ],
    disclaimer,
  },
]
